import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { Box, Button, Card, Column, Divider, Row, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { ColorSelectorWithPopup } from '~/client-flow/clientOb/BrandingControllers/ColorSelectorWithPopup';
import { PrimaryBackgroundColorPopup } from '~/client-flow/clientOb/BrandingControllers/PrimaryBackgroundColorPopup';
import { LogoPicker } from '~/components/LogoPicker/LogoPicker';
import { useLocaleKeys } from '~/i18n';
import { BrandPreview } from './BrandPreview/BrandPreview';
import styles from './BrandSettingsSection.module.scss';
import { useBrandSettingsForm } from './hooks/useBrandSettingsForm';
const testPrefix = 'settings-business-details-brand';
export const GetBrandSettingsSectionTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const BrandSettingsSection = (param)=>{
    let { testId = testPrefix, worksAt, onDirtyChange } = param;
    var _logo_asDataURL, _logo_asDataURL1;
    const { t } = useLocaleKeys('pages.businessDetails.brandSettings');
    const { t: pageTexts } = useLocaleKeys('pages.businessDetails');
    const testIds = useMemo(()=>GetBrandSettingsSectionTID(testId), [
        testId
    ]);
    const { BrandSettingsSectionTestIds } = testIds;
    const { branding, setBranding, logo, setLogo, isDirty, save, saving } = useBrandSettingsForm({
        worksAt
    });
    useEffect(()=>{
        onDirtyChange === null || onDirtyChange === void 0 ? void 0 : onDirtyChange(isDirty);
    }, [
        isDirty,
        onDirtyChange
    ]);
    const handleLogoChange = (data)=>{
        if (data) {
            setLogo({
                file: data.file,
                asDataURL: data.asDataURL
            });
        }
    };
    return /*#__PURE__*/ _jsxs(Card, {
        className: styles.card,
        autoHeight: true,
        padding: "m",
        disableSeparator: true,
        title: /*#__PURE__*/ _jsxs(Column, {
            gap: 4,
            children: [
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "h5",
                    weight: "normal",
                    children: t.title()
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "secondary",
                    children: t.subtitle()
                })
            ]
        }),
        testId: BrandSettingsSectionTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(Divider, {
                margin: 8
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: styles.body,
                gap: 24,
                alignItems: "stretch",
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        className: styles.leftColumn,
                        gap: 20,
                        children: [
                            /*#__PURE__*/ _jsx(LogoPicker, {
                                isNewUI: true,
                                image: (_logo_asDataURL = logo.asDataURL) !== null && _logo_asDataURL !== void 0 ? _logo_asDataURL : '',
                                onChange: handleLogoChange,
                                testId: "".concat(testId, "-logo"),
                                showEditButton: true,
                                editButtonLabel: t.changeLogo()
                            }),
                            /*#__PURE__*/ _jsxs(Column, {
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        weight: "normal",
                                        children: t.brandColors()
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 12,
                                        alignItems: "center",
                                        children: [
                                            /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                                                branding: branding,
                                                colorKey: "buttonPrimary",
                                                hasError: false,
                                                onChange: setBranding
                                            }),
                                            /*#__PURE__*/ _jsx(PrimaryBackgroundColorPopup, {
                                                branding: branding,
                                                hasError: false,
                                                onChange: setBranding
                                            }),
                                            /*#__PURE__*/ _jsx(ColorSelectorWithPopup, {
                                                branding: branding,
                                                colorKey: "backgroundStandard",
                                                hasError: false,
                                                onChange: setBranding
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.previewWrapper,
                        children: /*#__PURE__*/ _jsx(BrandPreview, {
                            testId: BrandSettingsSectionTestIds.Preview(),
                            image: (_logo_asDataURL1 = logo.asDataURL) !== null && _logo_asDataURL1 !== void 0 ? _logo_asDataURL1 : '',
                            brandColor: branding.buttonPrimary,
                            primaryBgStart: branding.backgroundGradientStart,
                            primaryBgEnd: branding.backgroundGradientEnd,
                            secondaryBgColor: branding.backgroundStandard,
                            loading: saving
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Box, {
                className: styles.saveButtonWrapper,
                children: /*#__PURE__*/ _jsx(Button, {
                    disabled: !isDirty || saving,
                    loading: saving,
                    onClick: save,
                    testId: BrandSettingsSectionTestIds.Save(),
                    bi: {
                        name: 'save_brand_settings'
                    },
                    children: pageTexts.saveChanges()
                })
            })
        ]
    });
};
